import React, { useRef, useState, useEffect } from 'react';
import { Rect, Text, Group, Transformer } from 'react-konva';

const COLORS = {
  '#FFEB3B': { text: '#000000', name: 'Yellow' },
  '#FFC107': { text: '#000000', name: 'Amber' },
  '#FF9800': { text: '#000000', name: 'Orange' },
  '#FF5722': { text: '#FFFFFF', name: 'Deep Orange' },
  '#E91E63': { text: '#FFFFFF', name: 'Pink' },
  '#9C27B0': { text: '#FFFFFF', name: 'Purple' },
  '#673AB7': { text: '#FFFFFF', name: 'Deep Purple' },
  '#3F51B5': { text: '#FFFFFF', name: 'Indigo' },
  '#2196F3': { text: '#FFFFFF', name: 'Blue' },
  '#03A9F4': { text: '#000000', name: 'Light Blue' },
  '#00BCD4': { text: '#000000', name: 'Cyan' },
  '#009688': { text: '#FFFFFF', name: 'Teal' },
  '#4CAF50': { text: '#FFFFFF', name: 'Green' },
  '#8BC34A': { text: '#000000', name: 'Light Green' },
  '#CDDC39': { text: '#000000', name: 'Lime' },
  '#FFFFFF': { text: '#000000', name: 'White' }
};

export default function StickyNote({ 
  element, 
  isSelected, 
  isBeingEdited,
  onSelect, 
  onDragEnd, 
  onTransformEnd,
  onUpdate,
  canEdit 
}) {
  const groupRef = useRef(null);
  const transformerRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const color = element.properties?.color || '#FFEB3B';
  const textColor = COLORS[color]?.text || '#000000';
  const text = element.properties?.text || '';
  
  // Attach transformer when selected
  useEffect(() => {
    if (isSelected && transformerRef.current && groupRef.current) {
      transformerRef.current.nodes([groupRef.current]);
      transformerRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  const handleDblClick = () => {
    if (!canEdit) return;
    setIsEditing(true);
    
    // Create textarea for editing
    const stage = groupRef.current.getStage();
    const stageBox = stage.container().getBoundingClientRect();
    const absPos = groupRef.current.getAbsolutePosition();
    const scale = stage.scaleX();
    
    const textarea = document.createElement('textarea');
    document.body.appendChild(textarea);
    
    textarea.value = text;
    textarea.style.position = 'absolute';
    textarea.style.left = `${stageBox.left + absPos.x * scale + 10}px`;
    textarea.style.top = `${stageBox.top + absPos.y * scale + 10}px`;
    textarea.style.width = `${(element.width - 20) * scale}px`;
    textarea.style.height = `${(element.height - 20) * scale}px`;
    textarea.style.fontSize = `${14 * scale}px`;
    textarea.style.fontFamily = 'Inter, system-ui, sans-serif';
    textarea.style.padding = '8px';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.resize = 'none';
    textarea.style.backgroundColor = color;
    textarea.style.color = textColor;
    textarea.style.lineHeight = '1.4';
    textarea.style.zIndex = '1000';
    textarea.style.borderRadius = '4px';
    
    textarea.focus();
    
    const handleBlur = () => {
      const newText = textarea.value;
      document.body.removeChild(textarea);
      setIsEditing(false);
      
      if (newText !== text) {
        onUpdate(element.id, { 
          properties: { ...element.properties, text: newText } 
        });
      }
    };
    
    textarea.addEventListener('blur', handleBlur);
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        textarea.blur();
      }
    });
  };

  return (
    <>
      <Group
        ref={groupRef}
        x={element.x}
        y={element.y}
        width={element.width}
        height={element.height}
        rotation={element.rotation || 0}
        scaleX={element.scale_x || 1}
        scaleY={element.scale_y || 1}
        draggable={canEdit}
        onClick={(e) => onSelect(element.id, e)}
        onTap={(e) => onSelect(element.id, e)}
        onDblClick={handleDblClick}
        onDblTap={handleDblClick}
        onDragStart={(e) => {
          e.cancelBubble = true;
        }}
        onDragEnd={(e) => {
          e.cancelBubble = true;
          onDragEnd(element.id, { x: e.target.x(), y: e.target.y() });
        }}
        onTransformEnd={() => {
          const node = groupRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          
          // Reset scale and update width/height
          node.scaleX(1);
          node.scaleY(1);
          
          onTransformEnd(element.id, {
            x: node.x(),
            y: node.y(),
            width: Math.max(50, node.width() * scaleX),
            height: Math.max(50, node.height() * scaleY),
            rotation: node.rotation()
          });
        }}
      >
        {/* Shadow */}
        <Rect
          x={3}
          y={3}
          width={element.width}
          height={element.height}
          fill="rgba(0,0,0,0.1)"
          cornerRadius={4}
        />
        
        {/* Main sticky */}
        <Rect
          width={element.width}
          height={element.height}
          fill={color}
          cornerRadius={4}
          stroke={isSelected ? '#3B82F6' : (isBeingEdited ? isBeingEdited.color : 'transparent')}
          strokeWidth={isSelected || isBeingEdited ? 2 : 0}
        />
        
        {/* Text content */}
        {!isEditing && (
          <Text
            x={10}
            y={10}
            width={element.width - 20}
            height={element.height - 20}
            text={text || 'Double-click to edit'}
            fontSize={14}
            fontFamily="Inter, system-ui, sans-serif"
            fill={text ? textColor : 'rgba(0,0,0,0.3)'}
            lineHeight={1.4}
            wrap="word"
          />
        )}
        
        {/* Edit indicator */}
        {isBeingEdited && (
          <Text
            x={5}
            y={element.height - 18}
            text={`${isBeingEdited.user_name} is editing...`}
            fontSize={10}
            fill={isBeingEdited.color}
            fontStyle="italic"
          />
        )}
      </Group>
      
      {isSelected && canEdit && (
        <Transformer
          ref={transformerRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 50 || newBox.height < 50) {
              return oldBox;
            }
            return newBox;
          }}
          rotateEnabled={true}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
        />
      )}
    </>
  );
}
